import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError, uploadFileToSignedUrl } from '../lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { LibraryAudience, LibraryDocument } from '../lib/types.ts';

const AUDIENCE_LABEL: Record<LibraryAudience, string> = {
  company: 'Companies',
  driver: 'Drivers',
  both: 'Companies & drivers',
};

export function AdminLibrary() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ['library'],
    queryFn: () => adminApi.get<{ documents: LibraryDocument[] }>('/api/library'),
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<LibraryDocument | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/library/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['library'] }),
  });

  if (isLoading) return <CenteredSpinner />;
  const documents = data?.documents ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Document library</h1>
          <p className="mt-1 text-sm text-slate-500">
            Reference documents (handbooks, policies, inductions) companies and drivers can view
            in their portals. These are read-only — not documents they upload for verification.
          </p>
        </div>
        <Btn onClick={() => setUploadOpen(true)}>+ Add document</Btn>
      </div>

      {documents.length === 0 ? (
        <EmptyState
          title="No library documents yet"
          description="Publish a handbook or policy that companies and drivers can read."
          action={<Btn onClick={() => setUploadOpen(true)}>+ Add document</Btn>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Audience</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {documents.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{d.title}</div>
                    {d.description && (
                      <div className="text-xs text-slate-500">{d.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{d.category ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-700">{AUDIENCE_LABEL[d.audience]}</td>
                  <td className="px-4 py-2">
                    {d.is_active ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        active
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        hidden
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="mr-2 text-xs text-blue-600 hover:underline"
                      onClick={async () => {
                        const res = await adminApi
                          .get<{ url: string }>(`/api/library/${d.id}/download-url`)
                          .catch(() => null);
                        if (res?.url) window.open(res.url, '_blank', 'noopener');
                      }}
                    >
                      Download
                    </button>
                    <button
                      className="mr-2 text-xs text-slate-600 hover:underline"
                      onClick={() => setEditing(d)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => {
                        void (async () => {
                          if (
                            await confirm({
                              title: 'Delete library document',
                              body: (
                                <>
                                  Delete <span className="font-semibold">{d.title}</span>? This
                                  can&apos;t be undone.
                                </>
                              ),
                              confirmLabel: 'Delete',
                              danger: true,
                            })
                          )
                            deleteMutation.mutate(d.id);
                        })();
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteMutation.error && <ErrorMessage error={deleteMutation.error} />}

      <UploadLibraryModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
      {editing && <EditLibraryModal doc={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function UploadLibraryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<{
    title: string;
    category: string;
    description: string;
    audience: LibraryAudience;
  }>({ title: '', category: '', description: '', audience: 'both' });
  const [step, setStep] = useState<'idle' | 'uploading' | 'registering'>('idle');
  const [error, setError] = useState<unknown>(null);

  function reset() {
    setFile(null);
    setMeta({ title: '', category: '', description: '', audience: 'both' });
  }

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      setStep('uploading');
      const presigned = await adminApi.post<{ path: string; url: string }>('/api/library/upload-url', {
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
      });
      await uploadFileToSignedUrl(presigned.url, file);
      setStep('registering');
      await adminApi.post('/api/library', {
        title: meta.title || file.name,
        description: meta.description.trim() || null,
        category: meta.category.trim() || null,
        audience: meta.audience,
        file_path: presigned.path,
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      });
      await qc.invalidateQueries({ queryKey: ['library'] });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setStep('idle');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (step === 'idle') {
          reset();
          onClose();
        }
      }}
      title="Add library document"
      footer={
        <>
          <Btn variant="ghost" onClick={() => { reset(); onClose(); }} disabled={step !== 'idle'}>
            Cancel
          </Btn>
          <Btn onClick={submit} loading={step !== 'idle'} disabled={!file || !meta.title.trim()}>
            Add
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <FileDrop
          multiple={false}
          onFiles={(files) => {
            const f = files.item(0);
            if (f) {
              setFile(f);
              setMeta((m) => ({ ...m, title: m.title || f.name.replace(/\.[^.]+$/, '') }));
            }
          }}
          hint="PDF, DOCX, or image"
        />
        {file && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        <Field label="Title" required>
          <TextInput value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <TextInput
              value={meta.category}
              onChange={(e) => setMeta({ ...meta, category: e.target.value })}
              placeholder="e.g. Handbook"
            />
          </Field>
          <Field label="Audience">
            <Select
              value={meta.audience}
              onChange={(e) => setMeta({ ...meta, audience: e.target.value as LibraryAudience })}
            >
              <option value="both">Companies &amp; drivers</option>
              <option value="company">Companies only</option>
              <option value="driver">Drivers only</option>
            </Select>
          </Field>
        </div>
        <Field label="Description">
          <TextArea
            rows={2}
            value={meta.description}
            onChange={(e) => setMeta({ ...meta, description: e.target.value })}
          />
        </Field>
        {step !== 'idle' && <div className="text-xs text-slate-500">Saving…</div>}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

function EditLibraryModal({ doc, onClose }: { doc: LibraryDocument; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: doc.title,
    category: doc.category ?? '',
    description: doc.description ?? '',
    audience: doc.audience,
    is_active: doc.is_active,
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () =>
      adminApi.patch(`/api/library/${doc.id}`, {
        title: form.title.trim(),
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        audience: form.audience,
        is_active: form.is_active,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['library'] });
      onClose();
    },
    onError: setError,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${doc.title}`}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={() => save.mutate()} loading={save.isPending} disabled={!form.title.trim()}>
            Save
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" required>
          <TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <TextInput value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </Field>
          <Field label="Audience">
            <Select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value as LibraryAudience })}
            >
              <option value="both">Companies &amp; drivers</option>
              <option value="company">Companies only</option>
              <option value="driver">Drivers only</option>
            </Select>
          </Field>
        </div>
        <Field label="Description">
          <TextArea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Visible in portals
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
